/**
 * GAME 1 — RÉSEAU (tuyaux à tourner). Arbre couvrant « growing tree » (branchu
 * selon le niveau) + brouillage à plancher contrôlé. Durci vs le prototype :
 * budget Compte-tours proportionnel à l'optimum, défis aveugles sans signal de
 * fuite rouge, Marathon sur-brouillé manche après manche, `par` = optimum de
 * quarts de tour posé pour tous les défis.
 */
import type { Diff, GameEngine, PuzzleBase, Rng } from '@ordre-cache/shared';
import { ri } from '@ordre-cache/shared';
import { D4, deep, mk } from './util';

export interface ReseauPuzzle extends PuzzleBase {
  g: 'reseau';
  wrap: boolean;
  blind: boolean;
  sol: number[][];
  cur: number[][];
  taps: number;
  budget?: number;
  round?: number;
  rounds?: number;
}

const rot1 = (m: number): number => ((m << 1) | (m >> 3)) & 15;
const rotk = (m: number, k: number): number => {
  k &= 3;
  while (k-- > 0) m = rot1(m);
  return m;
};
const PAL = [
  '#ef476f',
  '#ffd166',
  '#06d6a0',
  '#4cc9f0',
  '#b388ff',
  '#ff9e6d',
  '#f15bb5',
  '#80ed99',
];
const CURVE_KIND: Record<string, string> = { '3': 'TR', '6': 'BR', '12': 'BL', '9': 'TL' };

interface Geo {
  cc: [number, number];
  a0: number;
  a1: number;
}
function geo(kind: string, x0: number, y0: number, cs: number): Geo {
  switch (kind) {
    case 'TL':
      return { cc: [x0, y0], a0: 0, a1: Math.PI / 2 };
    case 'BR':
      return { cc: [x0 + cs, y0 + cs], a0: Math.PI, a1: Math.PI * 1.5 };
    case 'TR':
      return { cc: [x0 + cs, y0], a0: Math.PI / 2, a1: Math.PI };
    default:
      return { cc: [x0, y0 + cs], a0: Math.PI * 1.5, a1: Math.PI * 2 };
  }
}

/** Niveau → probabilité de brouillage, plancher de cellules fausses, branchement de l'arbre. */
const SCR_P = [0.6, 0.85, 1] as const;
const SCR_FLOOR = [0.5, 0.75, 0.95] as const;
const TREE_Q = [0, 0.35, 0.7] as const;

function reTree(N: number, wrap: boolean, rng: Rng, q: number): number[][] {
  const sol = mk(N, 0),
    seen = mk(N, false);
  const r = ri(rng, N),
    c = ri(rng, N);
  seen[r][c] = true;
  const st: [number, number][] = [[r, c]];
  while (st.length) {
    // « Growing tree » : avec probabilité q on étend depuis un élément ALÉATOIRE
    // de la pile (arbre plus branchu → plus de T ambigus) ; sinon DFS classique.
    const i = q > 0 && rng() < q ? ri(rng, st.length) : st.length - 1;
    const cur = st[i],
      o: [number, number, number][] = [];
    for (let d = 0; d < 4; d++) {
      let nr = cur[0] + D4[d][0],
        nc = cur[1] + D4[d][1];
      if (wrap) {
        nr = (nr + N) % N;
        nc = (nc + N) % N;
      } else if (nr < 0 || nr >= N || nc < 0 || nc >= N) continue;
      if (!seen[nr][nc]) o.push([d, nr, nc]);
    }
    if (!o.length) {
      st.splice(i, 1);
      continue;
    }
    const p = o[ri(rng, o.length)];
    sol[cur[0]][cur[1]] |= 1 << p[0];
    sol[p[1]][p[2]] |= 1 << ((p[0] + 2) & 3);
    seen[p[1]][p[2]] = true;
    st.push([p[1], p[2]]);
  }
  return sol;
}

interface ReInfo {
  leaks: number;
  leakCell: number[][];
  comp: number[][];
  components: number;
}
function reInfo(cur: number[][], N: number, wrap: boolean): ReInfo {
  const par = [...Array(N * N).keys()],
    sz = new Array(N * N).fill(1);
  const find = (x: number): number => {
    while (par[x] !== x) {
      par[x] = par[par[x]];
      x = par[x];
    }
    return x;
  };
  const uni = (a: number, b: number): void => {
    a = find(a);
    b = find(b);
    if (a !== b) {
      if (sz[a] < sz[b]) {
        const t = a;
        a = b;
        b = t;
      }
      par[b] = a;
      sz[a] += sz[b];
    }
  };
  let leaks = 0;
  const leakCell = mk(N, 0);
  for (let r = 0; r < N; r++)
    for (let c = 0; c < N; c++) {
      const m = cur[r][c];
      for (let d = 0; d < 4; d++)
        if (m & (1 << d)) {
          let nr = r + D4[d][0],
            nc = c + D4[d][1],
            ok;
          if (wrap) {
            nr = (nr + N) % N;
            nc = (nc + N) % N;
            ok = !!(cur[nr][nc] & (1 << ((d + 2) & 3)));
          } else
            ok = nr >= 0 && nr < N && nc >= 0 && nc < N && !!(cur[nr][nc] & (1 << ((d + 2) & 3)));
          if (ok) {
            if (d === 1 || d === 2) uni(r * N + c, nr * N + nc);
          } else {
            leaks++;
            leakCell[r][c] |= 1 << d;
          }
        }
    }
  const comp = mk(N, 0),
    roots = new Set<number>();
  for (let r = 0; r < N; r++)
    for (let c = 0; c < N; c++) {
      const rt = find(r * N + c);
      comp[r][c] = rt;
      roots.add(rt);
    }
  return { leaks, leakCell, comp, components: roots.size };
}

function reScramble(
  sol: number[][],
  N: number,
  pScr: number,
  rng: Rng,
  wrap: boolean,
  floor: number,
): number[][] {
  // Rotation UTILE par forme : droites (rot 2 = identité) → k ∈ {1,3} ;
  // croix (invariantes à toute rotation) → laisser ; autres → k ∈ {1,2,3}.
  const kick = (v: number): number => {
    if (rotk(v, 1) === v) return v;
    if (rotk(v, 2) === v) return rotk(v, ri(rng, 2) ? 3 : 1);
    return rotk(v, 1 + ri(rng, 3));
  };
  let nonInv = 0;
  for (let r = 0; r < N; r++)
    for (let c = 0; c < N; c++) if (rotk(sol[r][c], 1) !== sol[r][c]) nonInv++;
  const need = floor * nonInv;
  let best: number[][] | null = null,
    bestWrong = -1;
  for (let t = 0; t < 24; t++) {
    const cur = sol.map((row) => row.map((v) => (rng() < pScr ? kick(v) : v)));
    let wrong = 0;
    for (let r = 0; r < N; r++)
      for (let c = 0; c < N; c++) if (cur[r][c] !== sol[r][c]) wrong++;
    // Vérif « pas déjà résolu » avec le VRAI wrap : en tore, un état ≠ sol peut être résolu.
    const e = reInfo(cur, N, wrap);
    const unsolved = !(e.leaks === 0 && e.components === 1);
    if (unsolved && wrong >= need) return cur;
    if (unsolved && wrong > bestWrong) {
      bestWrong = wrong;
      best = cur;
    }
  }
  if (best) return best; // repli : meilleure tentative non résolue
  return sol.map((row) => row.map((v) => kick(v)));
}

/** Générateur commun à gen/advance : `floor` = plancher de brouillage (majoré en Marathon). */
function build(di: number, N: number, diff: Diff, rng: Rng, floor: number): ReseauPuzzle {
  const wrap = di === 2 || di === 4,
    blind = di === 1 || di === 4;
  // pScr doit couvrir le plancher demandé (planchers Marathon > pScr du niveau sinon).
  const pScr = Math.max(SCR_P[diff], Math.min(1, floor + 0.05));
  const sol = reTree(N, wrap, rng, TREE_Q[diff]);
  const cur = reScramble(sol, N, pScr, rng, wrap, floor);
  // Optimum de quarts de tour (cur est une rotation de sol : toujours atteint).
  let opt = 0;
  for (let r = 0; r < N; r++)
    for (let c = 0; c < N; c++)
      for (let k = 0; k < 4; k++)
        if (rotk(cur[r][c], k) === sol[r][c]) {
          opt += k;
          break;
        }
  const puz: ReseauPuzzle = {
    g: 'reseau',
    pid: ri(rng, 1e9),
    N,
    wrap,
    blind,
    di,
    sol,
    cur,
    taps: 0,
    par: opt,
  };
  if (di === 3)
    puz.budget = opt + Math.max([4, 3, 2][diff], Math.ceil(opt * [0.35, 0.18, 0.08][diff]));
  if (di === 5) {
    puz.round = 1;
    puz.rounds = 3;
  }
  return puz;
}

export const RESEAU: GameEngine<ReseauPuzzle> = {
  id: 'reseau',
  name: 'Réseau',
  icon: '🔌',
  accent: '#22e0a3',
  tag: 'Tourne les tuyaux, reconnecte le courant.',
  how: '<b>Touche une tuile</b> pour la tourner. But : former <b>UN seul réseau</b> sans aucun fil libre (bouts rouges). Les couleurs montrent les morceaux de réseau séparés.',
  sizes: [5, 10, 20, 30],
  defis: [
    { n: 'Connexion', d: 'La base : tourne chaque tuile pour reformer UN seul réseau.', ic: '🔗' },
    {
      n: "À l'aveugle",
      d: 'Ni couleurs, ni fuites rouges : seul le compteur de fils libres te guide.',
      ic: '🕶️',
    },
    { n: 'Sans frontières', d: 'Les bords se rejoignent : la grille tourne en boucle.', ic: '🌀' },
    { n: 'Compte-tours', d: 'Budget de rotations serré : réfléchis avant de tourner.', ic: '🧮' },
    {
      n: 'Nuit totale',
      d: 'Aveugle total sur une grille sans bords : ni couleurs, ni fuites, ni repères.',
      ic: '🌑',
    },
    { n: 'Marathon', d: "Trois réseaux d'affilée, de plus en plus brouillés.", ic: '🏃' },
  ],
  gen(di, N, diff, rng) {
    return build(di, N, diff, rng, SCR_FLOOR[diff]);
  },
  tap(p, r, c) {
    p.cur[r][c] = rot1(p.cur[r][c]);
    p.taps++;
    return true;
  },
  status(p) {
    const e = reInfo(p.cur, p.N, p.wrap);
    const solved = e.leaks === 0 && e.components === 1;
    let txt: string,
      ok = solved,
      prog = false;
    if (p.di === 3) {
      txt = `🔧 coups <b>${p.taps}/${p.budget}</b> · ${e.components} réseaux · ${e.leaks} fuites`;
      ok = solved && p.taps <= p.budget!;
      if (p.taps > p.budget! && !solved)
        txt = `💸 Budget dépassé (${p.taps}/${p.budget}) — 🔀 recommence`;
    } else if (p.di === 5) {
      txt = `🏁 Manche <b>${p.round}/${p.rounds}</b> · ${e.components} réseaux · ${e.leaks} fuites`;
      ok = solved && p.round === p.rounds;
      prog = solved && p.round! < p.rounds!;
    } else {
      const comps = p.blind ? '???' : e.components;
      txt = `🔗 <b>${comps}</b> réseaux séparés · <b>${e.leaks}</b> fils libres`;
    }
    if (ok) txt = '🎉 <b>Tout est relié !</b>' + (p.di === 5 ? ' Marathon terminé !' : '');
    else if (prog) txt = `✅ Manche ${p.round} réussie ! Touche <b>⏭</b> pour la suivante.`;
    return { ok, txt, prog };
  },
  extra(p) {
    const st = this.status(p);
    if (p.di === 5 && st.prog) return { label: `⏭ Manche ${p.round! + 1}/${p.rounds}` };
    return null;
  },
  advance(p, diff, rng) {
    const round = p.round! + 1;
    // Manches 2-3 sur-brouillées : plancher majoré de +0.15 par manche.
    const floor = Math.min(0.98, SCR_FLOOR[diff] + 0.15 * (round - 1));
    const next = build(5, p.N, diff, rng, floor);
    next.round = round;
    next.rounds = 3;
    return next;
  },
  draw(p, ctx, layout) {
    const S = layout.size,
      N = p.N,
      cs = S / N,
      cur = p.cur;
    ctx.fillStyle = '#0e0a1e';
    ctx.fillRect(0, 0, S, S);
    ctx.strokeStyle = 'rgba(255,255,255,0.05)';
    ctx.lineWidth = 1;
    for (let i = 1; i < N; i++) {
      ctx.beginPath();
      ctx.moveTo(i * cs, 0);
      ctx.lineTo(i * cs, S);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(0, i * cs);
      ctx.lineTo(S, i * cs);
      ctx.stroke();
    }
    const e = reInfo(cur, N, p.wrap),
      solved = e.leaks === 0 && e.components === 1;
    const rootCol = new Map<number, string>();
    let ci = 0;
    const colFor = (rt: number): string => {
      if (!rootCol.has(rt)) rootCol.set(rt, PAL[ci++ % PAL.length]);
      return rootCol.get(rt)!;
    };
    const lw = Math.max(2, cs * 0.13),
      hub = Math.max(2.2, cs * 0.15);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    for (let r = 0; r < N; r++)
      for (let c = 0; c < N; c++) {
        const m = cur[r][c];
        if (!m) continue;
        const x0 = c * cs,
          y0 = r * cs,
          cx = x0 + cs / 2,
          cy = y0 + cs / 2;
        const col = solved ? '#22e0a3' : p.blind ? '#7f74b3' : colFor(e.comp[r][c]),
          lk = e.leakCell[r][c];
        const dirs: number[] = [];
        for (let d = 0; d < 4; d++) if (m & (1 << d)) dirs.push(d);
        const mid = (d: number): [number, number] =>
          d === 0 ? [cx, y0] : d === 1 ? [x0 + cs, cy] : d === 2 ? [cx, y0 + cs] : [x0, cy];
        ctx.lineWidth = lw;
        if (solved) {
          ctx.shadowColor = col;
          ctx.shadowBlur = cs * 0.16;
        } else ctx.shadowBlur = 0;
        const isCurve = dirs.length === 2 && ((dirs[0] + 2) & 3) !== dirs[1];
        // Aveugle réel : aucune fuite signalée (ni rouge, ni rendu segmenté des courbes).
        if (isCurve && (!lk || p.blind)) {
          const G = geo(CURVE_KIND[m], x0, y0, cs);
          ctx.beginPath();
          ctx.strokeStyle = col;
          ctx.arc(G.cc[0], G.cc[1], cs / 2, G.a0, G.a1);
          ctx.stroke();
        } else
          for (const d of dirs) {
            const pt = mid(d);
            ctx.beginPath();
            ctx.strokeStyle = lk & (1 << d) && !p.blind ? '#ff5470' : col;
            ctx.moveTo(cx, cy);
            ctx.lineTo(pt[0], pt[1]);
            ctx.stroke();
          }
        ctx.shadowBlur = 0;
        ctx.beginPath();
        ctx.fillStyle = col;
        ctx.arc(cx, cy, dirs.length === 1 ? hub * 1.15 : hub * 0.7, 0, Math.PI * 2);
        ctx.fill();
      }
    if (p.wrap) {
      ctx.fillStyle = 'rgba(255,209,102,.6)';
      ctx.font = `${Math.max(9, cs * 0.22)}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.fillText('⟲ les bords se rejoignent', S / 2, S - 5);
    }
  },
  solveIt(p) {
    p.cur = deep(p.sol);
    p.taps = Math.min(p.taps, 0);
    if (p.di === 5) p.round = p.rounds;
  },
};
